import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Zap, ZapOff, Plus, Trash2, Info, TrendingUp, Sparkles, Pencil, Check, X } from 'lucide-react';

// ── Synonym Group Row ─────────────────────────────────────────
function SynonymRow({ group, idx, onDelete, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    setEditValue(group.terms.join(', '));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditValue('');
  };

  const handleSave = () => {
    const terms = editValue.split(',').map((t) => t.trim()).filter(Boolean);
    if (terms.length < 2) return;
    onEdit(idx, terms);
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        data-testid={`synonym-row-editing-${idx}`}
        className="flex items-center gap-2 bg-gray-800/80 border border-indigo-500/50 rounded-xl px-4 py-3"
      >
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') cancelEdit();
          }}
          className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm placeholder-gray-600 outline-none focus:border-indigo-500"
          placeholder="término1, término2, término3"
        />
        <button
          data-testid={`save-edit-synonym-${idx}`}
          onClick={handleSave}
          className="text-indigo-400 hover:text-indigo-300 transition-colors"
          title="Guardar"
        >
          <Check size={16} />
        </button>
        <button
          onClick={cancelEdit}
          className="text-gray-500 hover:text-gray-300 transition-colors"
          title="Cancelar"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="synonym-row"
      className="flex items-center gap-3 bg-gray-800/60 border border-gray-700 rounded-xl px-4 py-3 group"
    >
      <div className="flex flex-wrap gap-2 flex-1">
        {group.terms.map((term, i) => (
          <span
            key={i}
            className="bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-xs font-medium px-3 py-1 rounded-full"
          >
            {term}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          data-testid={`edit-synonym-btn-${idx}`}
          onClick={startEdit}
          className="text-gray-500 hover:text-indigo-400 transition-colors"
          title="Editar grupo"
        >
          <Pencil size={14} />
        </button>
        <button
          data-testid="delete-synonym-btn"
          onClick={onDelete}
          className="text-gray-600 hover:text-red-400 transition-colors"
          title="Eliminar grupo"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Usage Progress ────────────────────────────────────────────
function UsageBar({ used, limit, percentage, atLimit, warning }) {
  const barColor = atLimit
    ? 'bg-red-500'
    : warning
    ? 'bg-amber-500'
    : 'bg-indigo-500';

  return (
    <div data-testid="ai-usage-bar" className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Búsquedas AI este mes</span>
        <span className={atLimit ? 'text-red-400 font-semibold' : warning ? 'text-amber-400 font-semibold' : 'text-white font-medium'}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      {warning && !atLimit && (
        <p className="text-amber-400 text-xs flex items-center gap-1.5">
          <Info size={12} /> Has usado el {percentage}% de tu cuota mensual
        </p>
      )}
      {atLimit && (
        <p className="text-red-400 text-xs flex items-center gap-1.5">
          <Info size={12} /> Límite mensual alcanzado. Las búsquedas usan el motor clásico hasta el próximo mes.
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function SearchEnginePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [synonyms, setSynonyms] = useState([]);
  const [newTermInput, setNewTermInput] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [stockBehavior, setStockBehavior] = useState('show_last');
  const [savingStock, setSavingStock] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, usageRes] = await Promise.all([
        api.get('/api/billing/subscription-status'),
        api.get('/api/search/ai-usage'),
      ]);
      setPlan(statusRes.data.plan || null);
      setAiEnabled(usageRes.data.ai_enabled || false);
      setAiUsage(usageRes.data);
      setSynonyms(usageRes.data.synonyms || []);
      setStockBehavior(usageRes.data.out_of_stock_behavior || 'show_last');
    } catch (e) {
      console.error('Error loading search settings', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStockBehavior = async (value) => {
    setSavingStock(true);
    const prev = stockBehavior;
    setStockBehavior(value);
    try {
      await api.put('/api/search/settings', { out_of_stock_behavior: value });
      flashMessage('Configuración de stock guardada');
    } catch (e) {
      setStockBehavior(prev);
      flashMessage('Error al guardar', true);
    } finally {
      setSavingStock(false);
    }
  };

  const handleToggleAI = async (enabled) => {
    setSaving(true);
    try {
      await api.put('/api/search/settings', { ai_enabled: enabled });
      setAiEnabled(enabled);
      flashMessage(enabled ? 'Sera AI activada' : 'Sera AI desactivada');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al cambiar configuración';
      flashMessage(msg, true);
      // Revert toggle
      setAiEnabled(!enabled);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSynonym = async () => {
    const terms = newTermInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (terms.length < 2) {
      flashMessage('Añade al menos 2 términos separados por coma', true);
      return;
    }
    const updated = [...synonyms, { terms }];
    setSynonyms(updated);
    setNewTermInput('');
    setAddingGroup(false);
    await saveSynonyms(updated);
    flashMessage('Sinónimos guardados');
  };

  const handleEditSynonym = async (idx, terms) => {
    const updated = synonyms.map((g, i) => (i === idx ? { terms } : g));
    setSynonyms(updated);
    await saveSynonyms(updated);
    flashMessage('Sinónimos actualizados');
  };

  const handleDeleteSynonym = async (idx) => {
    const updated = synonyms.filter((_, i) => i !== idx);
    setSynonyms(updated);
    await saveSynonyms(updated);
    flashMessage('Grupo eliminado');
  };

  const saveSynonyms = async (syns) => {
    try {
      await api.put('/api/search/settings', { synonyms: syns });
    } catch (e) {
      flashMessage('Error al guardar sinónimos', true);
    }
  };

  const flashMessage = (msg, isError = false) => {
    setSaveMessage({ text: msg, error: isError });
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const planHasAI = plan?.features?.ai_included === true;
  const planName = plan?.name || 'Sin plan';
  const aiLimit = aiUsage?.ai_searches_limit || 0;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-5">
          <div className="h-8 bg-gray-800 rounded w-1/3" />
          <div className="h-40 bg-gray-800 rounded-xl" />
          <div className="h-60 bg-gray-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Motor de búsqueda</h1>
        <p className="text-gray-400 mt-1 text-sm">
          Configura el motor clásico y activa Sera AI para tu tienda
        </p>
      </div>

      {/* Toast message */}
      {saveMessage && (
        <div
          className={`mb-5 px-4 py-3 rounded-xl text-sm font-medium border ${
            saveMessage.error
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      {/* ── IA del Buscador ── */}
      <div
        data-testid="sera-ai-section"
        className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center">
              <Sparkles size={20} className="text-indigo-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">IA del Buscador</h2>
              <p className="text-gray-500 text-xs mt-0.5">
                Claude reordena los 50 mejores resultados por intención semántica · <span className="text-gray-600">No afecta al Chat</span>
              </p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={`text-xs ${planHasAI ? 'border-indigo-500/40 text-indigo-300' : 'border-gray-700 text-gray-500'}`}
          >
            {planName}
          </Badge>
        </div>

        {planHasAI ? (
          <div className="space-y-5">
            {/* Toggle */}
            <div className="flex items-center justify-between bg-gray-800/60 rounded-xl px-4 py-4 border border-gray-700">
              <div className="flex items-center gap-3">
                {aiEnabled ? (
                  <Zap size={16} className="text-indigo-400" />
                ) : (
                  <ZapOff size={16} className="text-gray-500" />
                )}
                <div>
                  <p className="text-sm font-medium text-white">
                    {aiEnabled ? 'Sera AI activa' : 'Sera AI inactiva'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {aiEnabled
                      ? 'Los resultados pasan por Claude antes de mostrarse'
                      : 'Solo se usa el motor clásico'}
                  </p>
                </div>
              </div>
              <Switch
                data-testid="ai-toggle"
                checked={aiEnabled}
                onCheckedChange={handleToggleAI}
                disabled={saving}
              />
            </div>

            {/* Usage counter */}
            {aiEnabled && aiUsage && (
              <UsageBar
                used={aiUsage.ai_searches_used || 0}
                limit={aiLimit}
                percentage={aiUsage.percentage || 0}
                atLimit={aiUsage.at_limit}
                warning={aiUsage.warning}
              />
            )}

            {/* Info box */}
            <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 text-xs text-gray-400 space-y-1.5">
              <p className="flex items-center gap-2">
                <TrendingUp size={12} className="text-indigo-400 flex-shrink-0" />
                Solo se contabilizan las búsquedas que pasan por Claude
              </p>
              <p className="flex items-center gap-2">
                <TrendingUp size={12} className="text-indigo-400 flex-shrink-0" />
                Si Claude falla, el motor clásico actúa como fallback automático
              </p>
              <p className="flex items-center gap-2">
                <TrendingUp size={12} className="text-indigo-400 flex-shrink-0" />
                Al superar el límite, se usa el motor clásico automáticamente
              </p>
            </div>
          </div>
        ) : (
          /* No AI plan — upgrade CTA */
          <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-5 text-center">
            <p className="text-gray-300 text-sm mb-1">
              Tu plan <span className="text-white font-medium">{planName}</span> no incluye Sera AI
            </p>
            <p className="text-gray-500 text-xs mb-4">
              Actualiza a Search AI (49€/mes) para activar la búsqueda semántica con Claude
            </p>
            <Button
              data-testid="upgrade-to-ai-btn"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => (window.location.href = '/billing')}
            >
              <Sparkles size={14} className="mr-1.5" />
              Ver planes con AI
            </Button>
          </div>
        )}
      </div>

      {/* ── Comportamiento Productos Sin Stock ── */}
      <div
        data-testid="stock-behavior-section"
        className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Info size={20} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">Productos sin stock</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Cómo deben aparecer en los resultados de búsqueda
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {[
            {
              value: 'show_normal',
              label: 'Mostrar normalmente con etiqueta "Agotado"',
              desc: 'Aparecen mezclados entre los resultados con un badge visible',
            },
            {
              value: 'show_last',
              label: 'Mostrar al final con etiqueta "Agotado"',
              desc: 'Los productos con stock aparecen primero, los agotados al final',
            },
            {
              value: 'hide',
              label: 'No mostrar productos sin stock',
              desc: 'Los productos agotados se excluyen completamente de la búsqueda',
            },
          ].map((opt) => (
            <button
              key={opt.value}
              data-testid={`stock-behavior-${opt.value}`}
              onClick={() => !savingStock && handleStockBehavior(opt.value)}
              disabled={savingStock}
              className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-all ${
                stockBehavior === opt.value
                  ? 'border-amber-500/50 bg-amber-500/8'
                  : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'
              }`}
            >
              <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                stockBehavior === opt.value ? 'border-amber-400' : 'border-gray-600'
              }`}>
                {stockBehavior === opt.value && (
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                )}
              </div>
              <div>
                <p className={`text-sm font-medium ${stockBehavior === opt.value ? 'text-white' : 'text-gray-300'}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-600 mt-3 flex items-center gap-1.5">
          <Info size={11} />
          Productos con stock infinito activado (venta sin stock) siempre se tratan como disponibles
        </p>
      </div>

      {/* ── Motor Clásico Info ── */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <Zap size={20} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">Motor clásico</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Incluido en todos los planes — búsquedas ilimitadas
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            ['Fuzzy search', '"samsum" encuentra Samsung'],
            ['Stemming ES/EN/FR', '"correr" encuentra "running"'],
            ['Sinónimos', 'Configura tus propios grupos'],
            ['Autocompletado', 'Sugerencias desde 2 caracteres'],
          ].map(([feature, desc]) => (
            <div
              key={feature}
              className="bg-gray-800/50 border border-gray-700/60 rounded-xl px-4 py-3"
            >
              <p className="text-white text-sm font-medium">{feature}</p>
              <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
            </div>
          ))}
        </div>

        {/* Atlas Search setup note */}
        <div className="mt-4 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-xs text-amber-300/80">
          <p className="font-medium mb-1">Configuración requerida en MongoDB Atlas</p>
          <p className="text-amber-300/60">
            Para activar fuzzy search, stemming y autocompletado necesitas crear el índice{' '}
            <code className="bg-amber-500/10 px-1 py-0.5 rounded text-amber-200">
              sera_product_search_index
            </code>{' '}
            en tu cluster Atlas.{' '}
            <a
              href="https://www.mongodb.com/docs/atlas/atlas-search/create-index/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-amber-300 hover:text-amber-200"
            >
              Ver guía
            </a>
          </p>
        </div>
      </div>

      {/* ── Synonyms Manager ── */}
      <div
        data-testid="synonyms-section"
        className="bg-gray-900 border border-gray-800 rounded-2xl p-6"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-white font-semibold text-base">Gestor de sinónimos</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Define grupos de términos equivalentes para mejorar los resultados
            </p>
          </div>
          <Button
            data-testid="add-synonym-group-btn"
            size="sm"
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white text-xs"
            onClick={() => setAddingGroup(true)}
          >
            <Plus size={13} className="mr-1" />
            Añadir grupo
          </Button>
        </div>

        {/* Add group input */}
        {addingGroup && (
          <div
            data-testid="add-synonym-form"
            className="mb-4 bg-gray-800/60 border border-indigo-500/30 rounded-xl p-4"
          >
            <p className="text-xs text-gray-400 mb-2">
              Escribe los términos separados por coma (mínimo 2)
            </p>
            <div className="flex gap-2">
              <input
                data-testid="synonym-input"
                type="text"
                value={newTermInput}
                onChange={(e) => setNewTermInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSynonym();
                  if (e.key === 'Escape') {
                    setAddingGroup(false);
                    setNewTermInput('');
                  }
                }}
                placeholder="moto, motocicleta, ciclomotor"
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 outline-none focus:border-indigo-500"
                autoFocus
              />
              <Button
                data-testid="save-synonym-btn"
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleAddSynonym}
              >
                Guardar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-500 hover:text-gray-300"
                onClick={() => {
                  setAddingGroup(false);
                  setNewTermInput('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Synonym list */}
        {synonyms.length > 0 ? (
          <div className="space-y-2">
            {synonyms.map((group, idx) => (
              <SynonymRow
                key={idx}
                group={group}
                idx={idx}
                onDelete={() => handleDeleteSynonym(idx)}
                onEdit={handleEditSynonym}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-gray-600">
            <p className="text-sm">No hay sinónimos configurados</p>
            <p className="text-xs mt-1">
              Ejemplo: <span className="text-gray-500">funda, carcasa, cover</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
